import { TextSource } from './types';
import { CONTACT } from '../siteData';

/**
 * Texte der Kampagnen-Landingpage Miteinander38.
 *
 * Die Seite begleitet die Radio38-Sendung „Gesundheit+" und bewirbt
 * **ausschließlich** die 24-Stunden-Betreuung. Hauswirtschaft, stundenweise
 * Betreuung und Stellenangebote gehören bewusst nicht darauf: Wer über einen
 * Radiobeitrag herkommt, hat genau ein Anliegen, und jede weitere Abzweigung
 * kostet Anfragen.
 *
 * Die Titel und Begleittexte der vier Beiträge stammen aus den Aufnahmen selbst.
 * Der gesprochene Inhalt steht als Transkript unter jedem Beitrag; er liegt
 * bewusst nicht hier, sondern in `miteinander/transkripte.ts` — ein Transkript
 * gibt wieder, was gesendet wurde, und ist nichts, was im Bearbeiten-Modus
 * umformuliert werden sollte.
 */
export const MITEINANDER: TextSource = {
  // ── Rahmen ────────────────────────────────────────────────────────────────
  'm38.marke': { text: 'Miteinander38', max: 30 },
  'm38.marke.zusatz': { text: 'eine Initiative von Seniorenbetreuung Antczak', max: 70 },
  'm38.topbar': { text: 'Radio38 „Gesundheit+" · unsere Beiträge zum Nachhören', max: 90 },

  /**
   * Die Rufnummer der Kampagne. Steht getrennt von den Stammdaten, weil das
   * Konzept eine eigene Nummer für Miteinander38 empfiehlt — nur so lässt sich
   * messen, wie viele Anrufe aus dem Radio kommen. Bis es sie gibt, steht hier
   * dieselbe Nummer wie auf der Kundenwebsite.
   */
  'm38.telefon': { text: CONTACT.phoneLabel, max: 30 },

  // ── Hero ──────────────────────────────────────────────────────────────────
  'm38.hero.badge': { text: 'Bekannt aus „Gesundheit+" auf Radio38', max: 60 },
  'm38.hero.titel': { text: 'Damit Zuhause', max: 40 },
  'm38.hero.titel-akzent': { text: 'Zuhause bleibt.', max: 40 },
  'm38.hero.text':
    'Wenn Mutter oder Vater im Alltag immer mehr Unterstützung brauchen, muss ein Pflegeheim nicht die einzige Lösung sein. Wir organisieren persönliche 24-Stunden-Betreuung im vertrauten Zuhause — individuell, menschlich und mit Ansprechpartnern in der Region.',
  'm38.hero.cta': { text: 'Unsere Situation besprechen', max: 40 },
  'm38.hero.cta-telefon': { text: 'Direkt anrufen', max: 30 },
  'm38.hero.merkmal-1': { text: 'kostenlos', max: 24 },
  'm38.hero.merkmal-2': { text: 'unverbindlich', max: 24 },
  'm38.hero.merkmal-3': { text: 'persönlich', max: 24 },
  'm38.hero.karte.titel': { text: 'Aus Gifhorn. Für die Region 38.', max: 50 },
  'm38.hero.karte.text': {
    text: 'Persönliche Beratung für Familien in Gifhorn, Braunschweig, Wolfsburg, Peine, Celle und Umgebung.',
    max: 160,
  },

  // ── Wiedererkennung ───────────────────────────────────────────────────────
  'm38.sorgen.eyebrow': { text: 'Vielleicht kennen Sie das', max: 40 },
  'm38.sorgen.titel': 'Wenn aus kleinen Sorgen immer größere Fragen werden.',
  'm38.sorgen.text':
    'Viele Familien beschäftigen sich lange mit dem Thema, bevor sie Unterstützung suchen. Oft beginnt es mit einem Gefühl: So wie bisher geht es nicht mehr lange.',

  'm38.sorge-1.titel': { text: '„Wir machen uns zunehmend Sorgen."', max: 60 },
  'm38.sorge-1.text':
    'Mutter oder Vater kommt noch zurecht — aber immer häufiger stellt sich die Frage, wie lange noch.',
  'm38.sorge-2.titel': { text: '„Wir können nicht immer da sein."', max: 60 },
  'm38.sorge-2.text':
    'Beruf, eigene Familie und Entfernung machen eine tägliche Unterstützung durch Angehörige oft unmöglich.',
  'm38.sorge-3.titel': { text: '„Ein Pflegeheim möchten wir vermeiden."', max: 60 },
  'm38.sorge-3.text':
    'Viele ältere Menschen wünschen sich, so lange wie möglich in ihrer vertrauten Umgebung zu bleiben.',
  'm38.sorge-4.titel': { text: '„Wo sollen wir überhaupt anfangen?"', max: 60 },
  'm38.sorge-4.text':
    'Welche Betreuung passt? Was kostet sie? Wer organisiert alles — und wem können wir vertrauen?',

  // ── Was ist 24h-Betreuung ─────────────────────────────────────────────────
  'm38.erklaerung.eyebrow': { text: '24-Stunden-Betreuung verständlich erklärt', max: 60 },
  'm38.erklaerung.titel': 'Eine vertraute Person im vertrauten Zuhause.',
  'm38.erklaerung.text':
    'Bei der sogenannten 24-Stunden-Betreuung lebt eine sorgfältig ausgewählte Betreuungskraft mit im Haushalt und unterstützt im Alltag, im Haushalt sowie bei der persönlichen Versorgung und Körperpflege, etwa beim Waschen, Duschen und Ankleiden.',
  'm38.erklaerung.leistung-1': { text: 'Haushalt, Kochen & Einkaufen', max: 50 },
  'm38.erklaerung.leistung-2': { text: 'Gesellschaft & Tagesstruktur', max: 50 },
  'm38.erklaerung.leistung-3': { text: 'Waschen, Duschen & Ankleiden', max: 50 },
  'm38.erklaerung.leistung-4': { text: 'Mobilität & Begleitung im Alltag', max: 50 },
  'm38.erklaerung.hinweis':
    '**Wichtig:** „24-Stunden-Betreuung" bedeutet nicht 24 Stunden Arbeitszeit. Ruhezeiten und arbeitsrechtliche Vorgaben werden eingehalten. Medizinische Behandlungspflege kann bei Bedarf durch einen ambulanten Pflegedienst ergänzt werden.',

  // ── Warum Antczak ─────────────────────────────────────────────────────────
  'm38.vertrauen.eyebrow': { text: 'Warum Antczak?', max: 40 },
  'm38.vertrauen.titel': 'Betreuung ist Vertrauenssache.',
  'm38.vertrauen.text':
    'Deshalb begleiten wir Familien persönlich — vom ersten Gespräch über die Auswahl der Betreuungskraft bis zur laufenden Betreuung.',

  'm38.vertrauen-1.titel': { text: 'Persönliche Beratung', max: 40 },
  'm38.vertrauen-1.text':
    'Wir lernen Ihre familiäre Situation und Ihre Wünsche zunächst in Ruhe kennen.',
  'm38.vertrauen-2.titel': { text: 'Passende Betreuungskraft', max: 40 },
  'm38.vertrauen-2.text':
    'Auswahl nach Bedarf, Erfahrung, Sprache und persönlicher Passung zur Familie.',
  'm38.vertrauen-3.titel': { text: 'Eigene Strukturen', max: 40 },
  'm38.vertrauen-3.text':
    'Die Organisation läuft nicht über ein anonymes Internetportal, sondern über unsere eigenen Ansprechpartner und ausgewählte Partner.',
  'm38.vertrauen-4.titel': { text: 'Ansprechpartner vor Ort', max: 40 },
  'm38.vertrauen-4.text':
    'Auch nach Beginn der Betreuung bleiben wir für Familie und Betreuungskraft erreichbar.',
  'm38.vertrauen-5.titel': { text: 'Regional verwurzelt', max: 40 },
  'm38.vertrauen-5.text':
    'Aus Gifhorn — persönlich für Familien in der Region 38 und Umgebung.',

  /*
   * Die Zitate selbst stehen nicht hier, sondern unter `home.stimmen.*`: Es sind
   * dieselben echten Rückmeldungen wie auf der Kundenwebsite, und eine zweite
   * Kopie wären zwei Wahrheiten über denselben Menschen. Nur die Überschrift
   * gehört der Kampagne.
   */
  'm38.stimmen.eyebrow': { text: 'Was Familien sagen', max: 40 },
  'm38.stimmen.titel': 'Familien, die diesen Schritt schon gegangen sind.',

  // ── Ablauf ────────────────────────────────────────────────────────────────
  'm38.ablauf.eyebrow': { text: 'Der Weg zur Betreuung', max: 40 },
  'm38.ablauf.titel': 'So einfach kann der erste Schritt sein.',
  'm38.ablauf.text':
    'Sie müssen heute noch keine Entscheidung treffen. Im ersten Gespräch geht es zunächst darum, Ihre Situation zu verstehen.',
  'm38.schritt-1.titel': { text: 'Wir sprechen miteinander.', max: 50 },
  'm38.schritt-1.text':
    'Sie schildern uns unverbindlich, welche Unterstützung benötigt wird und welche Wünsche Sie haben.',
  'm38.schritt-2.titel': { text: 'Wir suchen die passende Lösung.', max: 50 },
  'm38.schritt-2.text':
    'Wir klären den Betreuungsbedarf und schlagen passende Betreuungskräfte für Ihre Familie vor.',
  'm38.schritt-3.titel': { text: 'Die Betreuung kann beginnen.', max: 50 },
  'm38.schritt-3.text':
    'Nach Ihrer Auswahl organisieren wir die weiteren Schritte und begleiten den Start persönlich.',
  'm38.ablauf.cta': { text: 'Kostenloses Erstgespräch vereinbaren', max: 50 },

  // ── Betreuungs-Check ──────────────────────────────────────────────────────
  'm38.check.eyebrow': { text: 'Kostenloser Betreuungs-Check', max: 40 },
  'm38.check.titel': 'Passt eine 24-Stunden-Betreuung zu unserer Situation?',
  'm38.check.text':
    'Beantworten Sie uns wenige Fragen. Wir melden uns persönlich und besprechen mit Ihnen, welche Lösung sinnvoll sein könnte.',
  'm38.check.vorteil-1.titel': { text: 'Keine Verpflichtung', max: 40 },
  'm38.check.vorteil-1.text': 'Das Gespräch ist kostenlos und unverbindlich.',
  'm38.check.vorteil-2.titel': { text: 'Persönliche Einschätzung', max: 40 },
  'm38.check.vorteil-2.text': 'Keine automatische Standardantwort — wir sprechen mit Ihnen.',
  'm38.check.vorteil-3.titel': { text: 'Auch zur ersten Orientierung', max: 40 },
  'm38.check.vorteil-3.text': 'Sie müssen noch keinen konkreten Starttermin kennen.',
  'm38.check.absenden': { text: 'Kostenlosen Rückruf anfordern', max: 40 },
  'm38.check.fussnote': {
    text: 'Unverbindlich. Keine Vertragsbindung. Wir melden uns persönlich.',
    max: 100,
  },

  // ── Radio38 ───────────────────────────────────────────────────────────────
  'm38.radio.eyebrow': { text: 'Radio38 „Gesundheit+"', max: 40 },
  'm38.radio.titel': 'Unsere Beiträge zum Nachhören.',
  'm38.radio.text':
    'Sie haben einen unserer Beiträge bei Radio38 gehört oder möchten ihn noch einmal in Ruhe hören? Hier finden Sie alle Beiträge rund um die Betreuung im eigenen Zuhause.',
  /*
   * Titel und Begleittexte stammen aus den Aufnahmen selbst (transkribiert am
   * 28.08.2026). Die vier Teile bauen aufeinander auf und verweisen aufeinander
   * — Teil 2 kündigt den Erfahrungsbericht in Teil 3 an. Die Reihenfolge ist
   * deshalb Inhalt und nicht Geschmack: nicht umsortieren.
   */
  'm38.radio.beitrag-1.titel': {
    text: 'Zu Hause bleiben statt ins Heim',
    max: 80,
    label: 'Beitrag 1 — Titel',
  },
  'm38.radio.beitrag-1.text': {
    text: 'Warum ein Pflegeheim nicht die einzige Möglichkeit ist — und warum jede Betreuung mit einem persönlichen Gespräch beginnt.',
    max: 170,
    label: 'Beitrag 1 — Beschreibung',
  },

  'm38.radio.beitrag-2.titel': {
    text: 'Wie die Betreuung im Alltag abläuft',
    max: 80,
    label: 'Beitrag 2 — Titel',
  },
  'm38.radio.beitrag-2.text': {
    text: 'Eine Betreuungskraft zieht mit ein, bleibt sechs Wochen und wechselt sich mit einer festen zweiten Kraft ab. Was sie übernimmt — und was nicht.',
    max: 170,
    label: 'Beitrag 2 — Beschreibung',
  },

  'm38.radio.beitrag-3.titel': {
    text: 'Eine Tochter erzählt',
    max: 80,
    label: 'Beitrag 3 — Titel',
  },
  'm38.radio.beitrag-3.text': {
    text: 'Britta Berger über vier Jahre Betreuung für ihre 90-jährige Mutter: die Skepsis am Anfang, das erste Gespräch und was sich seitdem geändert hat.',
    max: 170,
    label: 'Beitrag 3 — Beschreibung',
  },

  'm38.radio.beitrag-4.titel': {
    text: 'Betreuung bezahlbar machen',
    max: 80,
    label: 'Beitrag 4 — Titel',
  },
  'm38.radio.beitrag-4.text': {
    text: 'Pflege kostet. Roman Antczak über die Idee, Betreuungskräfte in der Nachbarschaft zu teilen und die Kosten auf mehrere Schultern zu verteilen.',
    max: 170,
    label: 'Beitrag 4 — Beschreibung',
  },
  'm38.radio.transkript': {
    text: 'Beitrag nachlesen',
    max: 40,
    label: 'Transkript — Beschriftung',
  },
  'm38.radio.transkript.hinweis': {
    text: 'Automatisch erstellt und redaktionell durchgesehen. Es gilt das gesprochene Wort.',
    max: 120,
    label: 'Transkript — Hinweis',
  },

  /*
   * Der Anschlusssatz gehört in den Fließtext, nicht auf die Schaltfläche: Als
   * Beschriftung war er 480 px breit und zog auf dem Telefon die ganze Seite in
   * die Breite — samt der fixierten Leiste unten, deren rechter Rand dadurch
   * aus dem Bild lief. So steht es auch im Konzept (Abschnitt 12): erst die
   * Frage, dann die kurze Handlungsaufforderung.
   */
  'm38.radio.anschluss': 'Sie haben sich in einem der Beiträge wiedererkannt?',
  'm38.radio.cta': { text: 'Kostenlose Beratung vereinbaren', max: 40 },

  // ── Ansprechpartner ───────────────────────────────────────────────────────
  'm38.personen.eyebrow': { text: 'Persönlich statt anonym', max: 40 },
  'm38.personen.titel': 'Bei uns sprechen Sie mit Menschen — nicht mit einem Callcenter.',
  'm38.personen.zitat':
    '„Jede Familie und jede Betreuungssituation ist anders. Deshalb nehmen wir uns zunächst Zeit, Ihre Situation kennenzulernen."',
  'm38.personen.text':
    'Wir begleiten Sie vom ersten Gespräch bis zum Beginn und während der laufenden Betreuung — mit persönlichen Ansprechpartnern aus unserer Region.',
  'm38.personen.cta': { text: 'Persönliches Gespräch vereinbaren', max: 45 },

  // ── Häufige Fragen ────────────────────────────────────────────────────────
  'm38.faq.eyebrow': { text: 'Häufige Fragen', max: 40 },
  'm38.faq.titel': 'Was Familien vor dem ersten Gespräch wissen möchten.',
  'm38.faq-1.frage': { text: 'Was kostet eine 24-Stunden-Betreuung?', max: 80 },
  'm38.faq-1.antwort':
    'Die tatsächlichen Kosten hängen unter anderem vom Betreuungsbedarf, den Anforderungen an die Betreuungskraft und der individuellen Situation ab. Im kostenlosen Erstgespräch können wir den Bedarf einordnen und Ihnen eine konkrete Orientierung geben.',
  'm38.faq-2.frage': { text: 'Wie schnell kann eine Betreuung beginnen?', max: 80 },
  'm38.faq-2.antwort':
    'Das hängt vor allem von Ihren Anforderungen und der Verfügbarkeit passender Betreuungskräfte ab. Wenn es kurzfristig gehen muss, sagen Sie uns das direkt im Betreuungs-Check oder am Telefon.',
  'm38.faq-3.frage': { text: 'Hilft die Betreuungskraft auch bei der Körperpflege?', max: 80 },
  'm38.faq-3.antwort':
    'Ja. Je nach vereinbartem Leistungsumfang unterstützt die Betreuungskraft bei der persönlichen Versorgung und Körperpflege — zum Beispiel beim Waschen, Duschen, An- und Auskleiden oder beim Toilettengang.',
  'm38.faq-4.frage': { text: 'Wohnt die Betreuungskraft bei der betreuten Person?', max: 80 },
  'm38.faq-4.antwort':
    'Ja. Für die Betreuungskraft wird ein eigenes Zimmer im Haushalt benötigt. Die konkrete Organisation und die Rahmenbedingungen besprechen wir im Erstgespräch.',
  'm38.faq-5.frage': {
    text: 'Bedeutet „24-Stunden-Betreuung", dass rund um die Uhr gearbeitet wird?',
    max: 90,
  },
  'm38.faq-5.antwort':
    'Nein. Die Bezeichnung beschreibt die Betreuungsform mit einer im Haushalt lebenden Betreuungskraft. Arbeits- und Ruhezeiten müssen eingehalten werden. Den konkreten Betreuungsumfang stimmen wir individuell mit Ihnen ab.',
  'm38.faq-6.frage': { text: 'Müssen wir uns nach dem Erstgespräch entscheiden?', max: 80 },
  'm38.faq-6.antwort':
    'Nein. Das erste Gespräch ist kostenlos und unverbindlich. Es dient zunächst dazu, Ihre Situation zu verstehen, Fragen zu beantworten und mögliche Lösungen aufzuzeigen.',

  // ── Abschluss ─────────────────────────────────────────────────────────────
  'm38.abschluss.titel': 'Sie wissen noch nicht, ob eine 24-Stunden-Betreuung die richtige Lösung ist?',
  'm38.abschluss.text':
    'Genau dafür ist unser erstes Gespräch da. Erzählen Sie uns, wie Ihre Situation aussieht — wir hören zu und besprechen mit Ihnen die nächsten sinnvollen Schritte.',
  'm38.abschluss.cta': { text: 'Kostenlos beraten lassen', max: 40 },

  // ── Fußzeile & Danke-Seite ────────────────────────────────────────────────
  'm38.footer.zusatz': { text: 'Miteinander38 · 24-Stunden-Betreuung in der Region', max: 70 },
  'm38.footer.unternehmen': { text: 'Unternehmenswebsite', max: 30 },

  'm38.danke.titel': 'Vielen Dank für Ihre Anfrage.',
  'm38.danke.text':
    'Wir melden uns persönlich bei Ihnen — in der Regel noch am selben oder am nächsten Werktag. Wenn es eilt, erreichen Sie uns auch direkt am Telefon.',
  'm38.danke.zurueck': { text: 'Zurück zur Startseite', max: 30 },
};

export default MITEINANDER;
